import BaseSection from '@/sections/base'
import AJAXKlaviyoForm from '@/core/ajaxKlaviyoForm'
import { wireNewsletterForm } from '@/core/wireNewsletterForm'
import NewsletterForm from '@/components/newsletterForm'
import { setAriaFlag } from '@/core/utils/a11y'
import FocusTrap from '@/core/focusTrap'
import type { ThemeEditorSectionUnloadEvent } from '@/types/shopify'

const selectors = {
  modal: '[data-component="newsletter-modal"]',
  close: '[data-close]',
  trigger: 'a[href="#newsletter-modal"]',
}

export default class NewsletterModalSection extends BaseSection {
  static TYPE = 'newsletter-modal'

  modal: HTMLElement | null
  newsletterForm: NewsletterForm | null
  ajaxForm: AJAXKlaviyoForm | null
  focusTrap: FocusTrap | null
  private boundClick: (e: MouseEvent) => void
  private boundKeyDown: (e: KeyboardEvent) => void

  constructor(container: HTMLElement) {
    super(container)

    this.modal = this.container.querySelector(selectors.modal)
    this.newsletterForm = null
    this.ajaxForm = null
    this.focusTrap = null

    const formEl = this.qs(NewsletterForm.SELECTOR)
    if (formEl) {
      this.newsletterForm = new NewsletterForm(formEl)
      this.ajaxForm = wireNewsletterForm(formEl, this.newsletterForm)
    }

    if (this.modal) {
      this.focusTrap = new FocusTrap(this.modal, { autofocus: true, returnFocus: true })
      this.modal.querySelectorAll(selectors.close).forEach(btn => {
        btn.addEventListener('click', () => this.close())
      })
    }

    this.boundClick = this.onDocumentClick.bind(this)
    this.boundKeyDown = this.onDocumentKeyDown.bind(this)
    document.addEventListener('click', this.boundClick)
    document.addEventListener('keydown', this.boundKeyDown)
  }

  get isOpen(): boolean {
    return this.modal?.getAttribute('aria-hidden') !== 'true'
  }

  open() {
    if (!this.modal) return
    setAriaFlag(this.modal, 'aria-hidden', false)
    this.modal.removeAttribute('inert')
    document.body.style.overflow = 'hidden'
    this.focusTrap?.activate()
  }

  close() {
    if (!this.modal) return
    setAriaFlag(this.modal, 'aria-hidden', true)
    this.modal.setAttribute('inert', '')
    document.body.style.overflow = ''
    this.focusTrap?.deactivate()
  }

  onDocumentClick(e: MouseEvent) {
    const trigger = (e.target as HTMLElement).closest<HTMLAnchorElement>(selectors.trigger)
    if (!trigger) return
    e.preventDefault()
    this.open()
  }

  onDocumentKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape' && this.isOpen) this.close()
  }

  onUnload(e: ThemeEditorSectionUnloadEvent) {
    document.removeEventListener('click', this.boundClick)
    document.removeEventListener('keydown', this.boundKeyDown)
    this.newsletterForm?.destroy()
    this.ajaxForm?.destroy()
    super.onUnload(e)
  }
}
